import React, { useEffect, useState } from "react";
import { Box, FormControlLabel, IconButton, Radio } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { palette } from "../../themes/theme";
import { useDispatch, useSelector } from "react-redux";
import {
  updateActiveCrimeType,
  updateShadingSettings,
} from "../../redux/slices/mapSlice";
import { showLoader } from "../../redux/slices/loaderSlice";
import {
  colorSchemeData,
  crimeRiskLayer,
  defaultCrimeRiskLayerData,
  defaultEnvRiskLayerData,
  rangeDefaultValues,
} from "../../utils/helper";

const RadioBox = ({
  label,
  value,
  checked,
  onChange,
  disabled,
  onClickConfiguration,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        background: palette.background.light,
        px: 1,
        opacity: !disabled ? 1 : 0.4,
      }}
    >
      <FormControlLabel
        control={
          <Radio
            checked={checked}
            onClick={onChange}
            value={value}
            name="crime-risk-reports"
            sx={{ color: palette.primary.main }}
          />
        }
        label={label}
      />
      {checked && (
        <IconButton onClick={onClickConfiguration}>
          <SettingsIcon fontSize="medium" color="primary" />
        </IconButton>
      )}
    </Box>
  );
};

export const updateLegendAsPalette = (
  storageData,
  layerData,
  isCrimeType = false
) => {
  const updatedShadingSettingsData = layerData?.map((item, index) => {
    const preferred = Array.isArray(storageData)
      ? storageData[index]
      : undefined;
    if (preferred && isCrimeType && preferred.id.includes("crimeRange")) {
      // preferred can be either a color string or an object { color, value }
      const color =
        typeof preferred === "string"
          ? preferred
          : preferred.color ?? preferred;
      const value =
        typeof preferred === "string"
          ? item.value
          : preferred.value ?? item.value;
      return {
        ...item,
        color,
        value: value !== undefined ? String(value) : item.value,
      };
    }

    // fallback to scheme colors if preferred missing for this index
    const scheme = isCrimeType
      ? defaultCrimeRiskLayerData
      : defaultEnvRiskLayerData;
    const schemeItem = scheme[index % scheme.length];
    return {
      ...item,
      color: schemeItem.color ?? schemeItem,
      // keep existing item.value as fallback
      value: item.value,
    };
  });
  return updatedShadingSettingsData;
};

const CrimeRiskReports = ({ handleOpenModal }) => {
  const activeCrimeType = useSelector((state) => state.map.activeCrimeType);
  const shadingSettings = useSelector((state) => state.map.shadingSettings);

  const dispatch = useDispatch();

  const [crimeRiskLayerState, setCrimeRiskLayerState] =
    useState(crimeRiskLayer);
  const [selectedCrimeLayer, setSelectedCrimeLayer] = useState(activeCrimeType);

  // try to read saved defaultKey from redux/shadingSettings or localStorage
  const getSavedDefaultKey = () => {
    const keyFromState = shadingSettings?.defaultKey;
    if (keyFromState) return keyFromState;
    try {
      const stored = localStorage.getItem("preferedColorPalette");
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed?.defaultKey || null;
    } catch (e) {
      return null;
    }
  };
  const savedDefaultKey = getSavedDefaultKey();

  const applyPresetToDefaultLayer = (presetKey) => {
    const preset = rangeDefaultValues[presetKey];
    if (!preset) return defaultCrimeRiskLayerData;
    return defaultCrimeRiskLayerData.map((item, i) => ({
      ...item,
      value: preset[i] !== undefined ? String(preset[i]) : item.value,
    }));
  };

  useEffect(() => {
    setCrimeRiskLayerState(crimeRiskLayer);
  }, []);

  useEffect(() => {
    setSelectedCrimeLayer(activeCrimeType);
  }, [activeCrimeType]);

  const handleSelectLayerChange = (e) => {
    dispatch(
      showLoader({
        showLoader: true,
        showBackDrop: true,
      })
    );

    // Prefer legendColorSchemeData from localStorage > redux shadingSettings > default
    let preferredLegend = null;
    try {
      const raw = localStorage.getItem("preferedColorPalette");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          Array.isArray(parsed?.legendColorSchemeData) &&
          parsed.legendColorSchemeData.length > 0
        ) {
          preferredLegend = parsed.legendColorSchemeData;
        }
      }
    } catch (err) {
      preferredLegend = null;
    }

    if (selectedCrimeLayer !== e.target.value) {
      let colorScheme = [];
      // 1) localStorage preferred legend (if present and gradings not enabled)
      if (preferredLegend && !shadingSettings?.useGradings) {
        colorScheme = updateLegendAsPalette(
          preferredLegend,
          defaultCrimeRiskLayerData,
          true
        );
      }
      // 2) redux shadingSettings.legendColorSchemeData (if present and gradings not enabled)
      else if (
        shadingSettings?.legendColorSchemeData &&
        Array.isArray(shadingSettings.legendColorSchemeData) &&
        shadingSettings.legendColorSchemeData.length > 0 &&
        !shadingSettings?.useGradings
      ) {
        colorScheme = updateLegendAsPalette(
          shadingSettings.legendColorSchemeData,
          defaultCrimeRiskLayerData,
          true
        );
      }
      // 3) fallback: selectedColorScheme or defaultCrimeRiskLayerData
      else {
        if (selectedCrimeLayer) {
          colorScheme = updateLegendAsPalette(
            defaultCrimeRiskLayerData,
            defaultCrimeRiskLayerData,
            true
          );
        } else {
          colorScheme = updateLegendAsPalette(
            shadingSettings.selectedColorScheme
              ? colorSchemeData[shadingSettings.selectedColorScheme]
              : defaultCrimeRiskLayerData,
            defaultCrimeRiskLayerData,
            true
          );
        }
      }

      localStorage.setItem(
        "preferedColorPalette",
        JSON.stringify({
          ...shadingSettings,
          defaultKey: savedDefaultKey,
          legendColorSchemeData: colorScheme,
        })
      );
      setSelectedCrimeLayer(e.target.value);
      dispatch(updateActiveCrimeType(e.target.value));
      dispatch(
        updateShadingSettings({
          ...shadingSettings,
          // keep local state in sync: clear defaultKey for environment layer

          legendColorSchemeData: [...colorScheme],
          useGradings:
            colorScheme?.length < 13 ? false : shadingSettings.useGradings,
        })
      );
    } else {
      dispatch(updateActiveCrimeType(null));
      setSelectedCrimeLayer(null);
    }
    setTimeout(() => {
      dispatch(
        showLoader({
          showLoader: false,
          showBackDrop: false,
        })
      );
    }, 2000);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {crimeRiskLayerState?.length > 0 &&
        crimeRiskLayerState.map((layer) => (
          <RadioBox
            key={layer.id}
            label={layer.title}
            value={layer.id}
            checked={selectedCrimeLayer === layer.id}
            disabled={layer.disable}
            onChange={handleSelectLayerChange}
            onClickConfiguration={handleOpenModal}
          />
        ))}
    </Box>
  );
};

export default CrimeRiskReports;
